import { apiClient } from './client';
import type {
  LibraryIndex, LibraryBuildingCard, LibraryBuildingDetail,
  LibraryResearchCard, LibraryResearchDetail, LibraryComponentCard,
  LibraryItemCard, LibraryItemDetail, LibraryShipCard, LibraryShipDetail,
  LibrarySimpleEntry, LibraryAffixGroup, LibraryAttributeGroup, LibraryComponentSet,
} from '@/types/library';

/**
 * API Bibliothèque (encyclopédie). Lecture publique (/api/v1/Library).
 * Les domaines sont ajoutés par phases.
 */

export async function fetchLibraryIndex(): Promise<LibraryIndex> {
  const { data } = await apiClient.get<LibraryIndex>('/api/v1/Library/index');
  return data;
}

export async function fetchLibraryBuildings(): Promise<LibraryBuildingCard[]> {
  const { data } = await apiClient.get<LibraryBuildingCard[]>('/api/v1/Library/buildings');
  return data;
}

export async function fetchLibraryBuilding(id: number): Promise<LibraryBuildingDetail> {
  const { data } = await apiClient.get<LibraryBuildingDetail>(`/api/v1/Library/buildings/${id}`);
  return data;
}

export async function fetchLibraryResearch(): Promise<LibraryResearchCard[]> {
  const { data } = await apiClient.get<LibraryResearchCard[]>('/api/v1/Library/research');
  return data;
}

export async function fetchLibraryResearchDetail(id: number): Promise<LibraryResearchDetail> {
  const { data } = await apiClient.get<LibraryResearchDetail>(`/api/v1/Library/research/${id}`);
  return data;
}

export async function fetchLibraryComponents(): Promise<LibraryComponentCard[]> {
  const { data } = await apiClient.get<LibraryComponentCard[]>('/api/v1/Library/components');
  return data;
}

/** Composants livrés au départ (montés sur les vaisseaux des kits de démarrage). */
export async function fetchLibraryStarterComponents(): Promise<LibraryComponentCard[]> {
  const { data } = await apiClient.get<LibraryComponentCard[]>('/api/v1/Library/components/starter');
  return data;
}

/** Pool d'affixes (attributs tirables) par famille de composant. */
export async function fetchLibraryComponentAffixes(): Promise<LibraryAffixGroup[]> {
  const { data } = await apiClient.get<LibraryAffixGroup[]>('/api/v1/Library/components/affixes');
  return data;
}

/** Glossaire des attributs (influence + où ils apparaissent), groupé par domaine. */
export async function fetchLibraryAttributes(): Promise<LibraryAttributeGroup[]> {
  const { data } = await apiClient.get<LibraryAttributeGroup[]>('/api/v1/Library/attributes');
  return data;
}

/** Sets de composants (membres + paliers de bonus). Public. */
export async function fetchLibraryComponentSets(): Promise<LibraryComponentSet[]> {
  const { data } = await apiClient.get<LibraryComponentSet[]>('/api/v1/Library/component-sets');
  return data;
}

/** Sets d'OBJETS (artefacts/reliques) — même forme que les sets de composants. Public. */
export async function fetchLibraryItemSets(): Promise<LibraryComponentSet[]> {
  const { data } = await apiClient.get<LibraryComponentSet[]>('/api/v1/Library/item-sets');
  return data;
}

export async function fetchLibraryItems(): Promise<LibraryItemCard[]> {
  const { data } = await apiClient.get<LibraryItemCard[]>('/api/v1/Library/items');
  return data;
}

export async function fetchLibraryItem(id: number): Promise<LibraryItemDetail> {
  const { data } = await apiClient.get<LibraryItemDetail>(`/api/v1/Library/items/${id}`);
  return data;
}

export async function fetchLibraryShips(): Promise<LibraryShipCard[]> {
  const { data } = await apiClient.get<LibraryShipCard[]>('/api/v1/Library/ships');
  return data;
}

export async function fetchLibraryShip(id: number): Promise<LibraryShipDetail> {
  const { data } = await apiClient.get<LibraryShipDetail>(`/api/v1/Library/ships/${id}`);
  return data;
}

export async function fetchLibraryCatalog(domain: string): Promise<LibrarySimpleEntry[]> {
  const { data } = await apiClient.get<LibrarySimpleEntry[]>(`/api/v1/Library/catalog/${domain}`);
  return data;
}
